import React, { useEffect, useState, useCallback } from 'react';
import { FileText, Calendar, User, Link as LinkIcon, Mail, Package, File } from 'lucide-react';
import { DiscrepancyService } from '../services/discrepancyService';
import { Modal, ModalBody, ModalFooter, Button, Badge } from './ui';
import { formatLastModified } from '../utils/dateFormatting';

const DiscrepancyDetailsModal = ({ discrepancyId, isOpen, onClose }) => {
  const [discrepancy, setDiscrepancy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadDiscrepancy = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await DiscrepancyService.getDiscrepancyById(discrepancyId);
      console.log('DiscrepancyDetailsModal - Raw data from API:', data);
      console.log('DiscrepancyDetailsModal - data.project:', data.project);
      console.log('DiscrepancyDetailsModal - data.projectId:', data.projectId);
      console.log('DiscrepancyDetailsModal - data.project_id:', data.project_id);

      // Map the nested project data to flat properties for easier access
      const mappedData = {
        ...data,
        projectName: data.project?.projectName || data.projectName || null,
        projectJobNumber: data.project?.jobNumber || data.projectJobNumber || null,
        projectRev: data.project?.rev || data.projectRev || null,
        clientName: data.project?.client?.name || data.clientName || null,
        // Don't overwrite if already in camelCase
        discrepancyType: data.discrepancyType || data.type,
        discrepancyStatus: data.discrepancyStatus || data.status,
        createdByName: data.created_by_name || data.createdBy,
        loomLink: data.loomLink || data.loom_link,
        markupAttachment: data.markupAttachment || data.markup_attachment,
        additionalInformation: data.additionalInformation || data.additional_information,
        sentDate: data.sentDate || data.sent_date,
        createdAt: data.createdAt || data.created_at,
        emailTo: data.emailTo || data.email_to,
        emailCc: data.emailCc || data.email_cc,
      };

      console.log('DiscrepancyDetailsModal - Mapped data:', mappedData);
      setDiscrepancy(mappedData);
    } catch (err) {
      console.error('Error loading discrepancy:', err);
      setError('Failed to load discrepancy details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [discrepancyId]);

  // Load discrepancy data when modal opens
  useEffect(() => {
    if (isOpen && discrepancyId) {
      loadDiscrepancy();
    }
  }, [isOpen, discrepancyId, loadDiscrepancy]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Draft': return 'neutral';
      case 'Waiting to Review': return 'danger';
      case 'Reviewed - not sent': return 'warning';
      case 'Sent': return 'info';
      case 'Resolved': return 'success';
      default: return 'neutral';
    }
  };

  // const getTypeIcon = (type) => {
  //   switch (type) {
  //     case 'Clarification': return '?';
  //     case 'Issue': return '⚠';
  //     case 'Error': return '✕';
  //     case 'Missing Info': return '📋';
  //     case 'Change Request': return '↻';
  //     default: return '!';
  //   }
  // };

  if (!discrepancy && !loading) return null;

  return (
    <Modal trackAs="DiscrepancyDetailsModal" isOpen={isOpen} onClose={onClose} size="xl">
      {/* Custom Header with Icon */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
          borderTopLeftRadius: 'var(--radius-xl)',
          borderTopRightRadius: 'var(--radius-xl)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Job Number and Project Name on one line */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
              <span
                style={{
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-400)',
                  flexShrink: 0,
                }}
              >
                {loading ? 'Loading...' : (
                  discrepancy?.projectJobNumber
                    ? `${discrepancy.projectJobNumber}${discrepancy.projectRev && parseInt(discrepancy.projectRev) > 0 ? ` Rev${discrepancy.projectRev}` : ''}`
                    : 'N/A'
                )}
              </span>
              <h1
                style={{
                  margin: 0,
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-50)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {loading ? '' : discrepancy?.projectName || 'Discrepancy'}
              </h1>
            </div>
            {/* Discrepancy Type below */}
            <p
              style={{
                margin: 0,
                marginTop: 'var(--space-1)',
                fontSize: 'var(--text-base)',
                color: 'var(--gray-400)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {loading ? 'Please wait...' : discrepancy?.discrepancyType || 'Discrepancy Details'}
            </p>
          </div>
        </div>
      </div>

      <ModalBody>
        {/* Error message */}
        {error && (
          <div
            style={{
              padding: 'var(--space-3)',
              marginBottom: 'var(--space-4)',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid var(--danger)',
              borderRadius: 'var(--radius-md)',
              color: 'var(--danger)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--gray-400)' }}>
            <div style={{ fontSize: 'var(--text-lg)' }}>Loading discrepancy details...</div>
          </div>
        ) : discrepancy ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
            {/* Left Column: Discrepancy Information */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Discrepancy Information
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {/* Status */}
                <div
                  style={{
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--dark-surface)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Status
                  </label>
                  <Badge variant={getStatusColor(discrepancy.discrepancyStatus)}>
                    {discrepancy.discrepancyStatus || 'Draft'}
                  </Badge>
                </div>

                {/* Type */}
                <div
                  style={{
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--dark-surface)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <FileText size={14} />
                    Type
                  </label>
                  <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                    {discrepancy.discrepancyType || 'Not specified'}
                  </div>
                </div>

                {/* Description */}
                <div
                  style={{
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--dark-surface)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Description
                  </label>
                  <div style={{ color: 'var(--gray-300)', fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-relaxed)', whiteSpace: 'pre-wrap' }}>
                    {discrepancy.description || 'No description provided'}
                  </div>
                </div>

                {/* Created Info */}
                <div
                  style={{
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--dark-surface)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <User size={14} />
                    Created By
                  </label>
                  <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)' }}>
                    {discrepancy.createdByName || 'Unknown'}
                  </div>
                  {discrepancy.createdAt && (
                    <div style={{ color: 'var(--gray-400)', fontSize: 'var(--text-xs)', marginTop: 'var(--space-1)' }}>
                      {formatLastModified(discrepancy.createdAt)}
                    </div>
                  )}
                </div>

                {/* Sent Date */}
                {discrepancy.sentDate && (
                  <div
                    style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      <Calendar size={14} />
                      Sent Date
                    </label>
                    <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-sm)' }}>
                      {formatLastModified(discrepancy.sentDate)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Project & Details */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Project & Details
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {/* Project */}
                <div
                  style={{
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--dark-surface)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <Package size={14} />
                    Project
                  </label>
                  <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                    {discrepancy.projectName || 'Not specified'}
                  </div>
                  {discrepancy.projectJobNumber && (
                    <div style={{ color: 'var(--gray-400)', fontSize: 'var(--text-sm)', marginTop: 'var(--space-1)' }}>
                      Job #{discrepancy.projectJobNumber}{discrepancy.projectRev && parseInt(discrepancy.projectRev) > 0 ? ` Rev${discrepancy.projectRev}` : ''}
                    </div>
                  )}
                </div>

                {/* Loom Link */}
                {discrepancy.loomLink && (
                  <div
                    style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      <LinkIcon size={14} />
                      Loom Video
                    </label>
                    <a
                      href={discrepancy.loomLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--cta-blue)', fontSize: 'var(--text-sm)', textDecoration: 'none', fontWeight: 'var(--font-medium)' }}
                      onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
                      onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
                    >
                      View Loom Video
                    </a>
                  </div>
                )}

                {/* Markup Attachments */}
                {discrepancy.markupAttachment && Array.isArray(discrepancy.markupAttachment) && discrepancy.markupAttachment.length > 0 && (
                  <div
                    style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Attachments ({discrepancy.markupAttachment.length})
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                      {discrepancy.markupAttachment.map((file, index) => {
                        const fileName = typeof file === 'string' ? file : (file.name || file.filename || `File ${index + 1}`);
                        const fileUrl = typeof file === 'string' ? file : file.url;

                        return fileUrl ? (
                          <a
                            key={index}
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            style={{
                              color: 'var(--cta-blue)',
                              fontSize: 'var(--text-sm)',
                              textDecoration: 'none',
                              fontWeight: 'var(--font-medium)',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 'var(--space-2)',
                              transition: 'all 0.15s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.textDecoration = 'underline';
                              e.target.style.color = 'var(--cta-blue-hover, #2563eb)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.textDecoration = 'none';
                              e.target.style.color = 'var(--cta-blue)';
                            }}
                          >
                            <File size={16} style={{ flexShrink: 0, color: 'var(--gray-200)' }} />
                            {fileName}
                          </a>
                        ) : (
                          <div key={index} style={{ color: 'var(--gray-300)', fontSize: 'var(--text-sm)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <File size={16} style={{ flexShrink: 0, color: 'var(--gray-200)' }} />
                            {fileName}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Additional Information */}
                {discrepancy.additionalInformation && (
                  <div
                    style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Additional Information
                    </label>
                    <div style={{ color: 'var(--gray-300)', fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-relaxed)', whiteSpace: 'pre-wrap' }}>
                      {discrepancy.additionalInformation}
                    </div>
                  </div>
                )}

                {/* Email Status */}
                {(discrepancy.emailTo && discrepancy.emailTo.length > 0) && (
                  <div
                    style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      <Mail size={14} />
                      Email Recipients
                    </label>
                    <div style={{ color: 'var(--gray-300)', fontSize: 'var(--text-sm)' }}>
                      {discrepancy.emailTo.join(', ')}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </ModalBody>

      <ModalFooter>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          <Button variant="ghost" size="md" onClick={onClose}>
            Close
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default DiscrepancyDetailsModal;
